import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { DataSource, ProjectIntent, ProjectPhase, SiteRagReport } from '../../types/siteRag';
import type { ReportClaim } from '../../types/citation';
import { effectivePhaseCapacity, phaseIncrementMW } from '../projectIntent';
import { resolveDevelopmentPathwayPhase } from '../reconcileDevelopmentPathway';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { buildNumericSourceEvidence } from '../grounding/numericSourceEvidence';

export interface ScopedClaimSourceContext {
  sed: Partial<Pick<SiteEvidenceDocument, 'id' | 'fullProjectIntent'>>;
  siteId?: string;
  projectIntent?: ProjectIntent | null;
  report: unknown;
  references?: readonly DataSource[];
}
const MARKER = /\[(?:verified\s+)?sed-section:(projectIntent|projectIntentOverview)\]/gi;
const clean = (text: string) => text.replace(MARKER, '').replace(/\s+([.,;:!?])/g, '$1').replace(/\s+/g, ' ').trim();
const rowObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const phaseIdentity = (phase: ProjectPhase) => [phase.id, phase.sequence, phase.label, phase.capacityMW, phase.capacityBucket,
  phase.capacitySource, phase.powerSource, phase.targetInServiceDate, phase.notes];
const numeric = (value: string) => Number(value.replace(/,/g, ''));

/** Resolve only an actual owner phase, never a row ordinal or a matching MW.
 * The resolved intent must still match the frozen owner inputs in this scope. */
function ownerPhase(text: string, fieldPath: string, context: ScopedClaimSourceContext) {
  const frozen = context.sed.fullProjectIntent;
  const intent = context.projectIntent;
  if (!frozen?.phases?.length || !intent?.phases?.length || !rowObject(context.report)) return null;
  if (context.siteId && context.report.siteId && context.siteId !== context.report.siteId) return null;
  if (context.sed.id && context.report.sedId && context.sed.id !== context.report.sedId) return null;
  if (frozen.powerBasis !== intent.powerBasis || isIntentFieldExplicitlyUnknown(frozen, 'powerBasis') !== isIntentFieldExplicitlyUnknown(intent, 'powerBasis')) return null;
  const sorted = frozen.phases.slice().sort((a, b) => a.sequence - b.sequence);
  if (new Set(sorted.map(p => p.id)).size !== sorted.length || new Set(sorted.map(p => p.sequence)).size !== sorted.length) return null;
  if (JSON.stringify(sorted.map(phaseIdentity)) !== JSON.stringify(intent.phases.slice().sort((a, b) => a.sequence - b.sequence).map(phaseIdentity))) return null;
  const rows = context.report.developmentPathway;
  if (!Array.isArray(rows)) return null;
  const index = /^developmentPathway\[(\d+)\]\.description$/.exec(fieldPath)?.[1];
  if (index == null && fieldPath !== 'developmentPathway') return null;
  const candidates = (index == null ? rows : [rows[Number(index)]])
    .filter((row): row is Record<string, unknown> => rowObject(row) && typeof row.phase === 'string' && typeof row.title === 'string'
      && typeof row.description === 'string' && clean(row.description).includes(clean(text)))
    .map(row => resolveDevelopmentPathwayPhase(row as unknown as NonNullable<SiteRagReport['developmentPathway']>[number], sorted))
    .filter((phase): phase is ProjectPhase => !!phase);
  if (candidates.length !== 1) return null;
  const phase = candidates[0]!;
  const position = sorted.findIndex(row => row.id === phase.id);
  const sourceIndex = frozen.phases.findIndex(row => row.id === phase.id);
  return { frozen, sorted, phase, position, sourceIndex, prefix: `sed:fullProjectIntent.phases.${sourceIndex}` };
}

/** A token names a proposed source, not proof. Recognize a finite set of owner
 * planning statements and check every value and scope against the frozen phase.
 * Extra assertions, guarantees, equipment MW and arbitrary SED paths get none. */
export function resolveScopedClaimSources(text: string, fieldPath: string, context?: ScopedClaimSourceContext): string[] {
  if (!context || typeof text !== 'string' || ![...text.matchAll(MARKER)].length) return [];
  // Reject mixed/internal aliases rather than crediting a valid marker beside
  // an unrelated source path or another assertion.
  if (/\[(?!verified\s+sed-section:(?:projectIntent|projectIntentOverview)\]|sed-section:(?:projectIntent|projectIntentOverview)\])[^\]]+\]/i.test(text)) return [];
  const owner = ownerPhase(text, fieldPath, context);
  if (!owner) return [];
  const { phase, sorted, position, sourceIndex, prefix, frozen } = owner;
  const prose = clean(text);
  const capacity = effectivePhaseCapacity(phase);
  const knownBasis = !isIntentFieldExplicitlyUnknown(frozen, 'powerBasis') && ['it-load', 'facility'].includes(frozen.powerBasis ?? '');
  const basisMatches = (basis: string) => knownBasis && (basis.toLowerCase() === 'it' ? frozen.powerBasis === 'it-load' : frozen.powerBasis === 'facility');
  const initial = /^Establishes the initial ([\d,]+(?:\.\d+)?) MW of cumulative campus (IT|facility)[- ]load(?:, per Project Intent)?\.$/i.exec(prose);
  if (initial && position === 0 && capacity.isExact && capacity.mw === numeric(initial[1]!) && basisMatches(initial[2]!)) return [`${prefix}.capacityMW`, 'sed:fullProjectIntent.powerBasis'];
  const addition = /^Adds ([\d,]+(?:\.\d+)?) MW of (IT|facility) load(?: for the final buildout)?(?:, bringing (?:the cumulative campus total to|the campus to its ultimate planned capacity of) ([\d,]+(?:\.\d+)?) MW)?(?:, per Project Intent)?\.$/i.exec(prose);
  if (addition && capacity.isExact && basisMatches(addition[2]!) && (!addition[3] || capacity.mw === numeric(addition[3]))) {
    // The general arithmetic helper treats a missing predecessor as zero;
    // citation proof deliberately requires the predecessor to be known.
    if (position > 0 && !effectivePhaseCapacity(sorted[position - 1]!).isExact) return [];
    if (/final buildout|ultimate planned/i.test(prose) && position !== sorted.length - 1) return [];
    if (phaseIncrementMW(sorted, position) !== numeric(addition[1]!)) return [];
    return [`${prefix}.capacityMW`, ...(position > 0 ? [`sed:fullProjectIntent.phases.${frozen.phases!.findIndex(row => row.id === sorted[position - 1]!.id)}.capacityMW`] : []), 'sed:fullProjectIntent.powerBasis'];
  }
  if (/^The power source remains undecided(?:, per Project Intent)?\.$/i.test(prose) && phase.powerSource === 'undecided') return [`${prefix}.powerSource`];
  const target = /^This phase is targeted for (?:Q([1-4]) )?(20\d{2})(?:, but the power source remains undecided)?\.$/i.exec(prose);
  if (target) {
    if (/power source remains undecided/i.test(prose) && phase.powerSource !== 'undecided') return [];
    const targets = buildNumericSourceEvidence({ frozenProjectIntent: frozen }).phaseTargets?.filter(row => row.phaseSequence === phase.sequence) ?? [];
    if (targets.length !== 1 || targets[0]!.year !== Number(target[2]) || (target[1] && targets[0]!.quarter !== Number(target[1]))) return [];
    return [`${prefix}.${phase.targetInServiceDate ? 'targetInServiceDate' : 'notes'}`, ...(/power source remains undecided/i.test(prose) ? [`${prefix}.powerSource`] : [])];
  }
  // sourceIndex is intentionally derived from the frozen array, not the report
  // row index; work stages often precede the owner milestones in a report.
  void sourceIndex;
  return [];
}

/** Fill only an existing, exact phase-marker claim's missing attachment. This
 * changes no text or claim membership. A mismatched supplied source is retained
 * for normal validation, not replaced or laundered through a valid marker. */
export function reconcileScopedClaimSources(report: unknown, context: Omit<ScopedClaimSourceContext, 'report'>): ReportClaim[] | undefined {
  if (!rowObject(report) || !Array.isArray(report.claims)) return undefined;
  return report.claims.map((claim: ReportClaim) => {
    if (!claim || typeof claim.text !== 'string' || typeof claim.sectionId !== 'string' || !Array.isArray(claim.sources) || claim.sources.length) return claim;
    const sources = resolveScopedClaimSources(claim.text, claim.sectionId, { ...context, report });
    return sources.length ? { ...claim, sources } : claim;
  });
}
